import apiClient, { ApiResponse, PaginatedResponse } from './client';
import type {
  CarryOverAnchor,
  PlacementMm,
  ProductClass,
} from '@designdeck/shared';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

/**
 * Design status enum
 */
export type DesignStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

/**
 * Design interface
 * canvasData is the Source of Truth (Fabric.js JSON)
 */
/** State of one listing on one publish channel, as the overview shows it. */
export interface DesignChannelPublication {
  channelId: string;
  status: 'OPEN' | 'RUNNING' | 'PULLED' | 'PUBLISHED' | 'FAILED';
  failReasonCode: string | null;
  failReasonDetail: string | null;
  finishedAt: string | null;
  items: Array<{
    designProductId: string;
    status: 'OPEN' | 'RUNNING' | 'PULLED' | 'PUBLISHED' | 'FAILED';
    targetId: string | null;
    targetUrl: string | null;
    failReasonCode: string | null;
  }>;
}

/** One reason a design's products keep it from going live (GET /designs/:id). */
export type CalibrationBlocker =
  | { code: 'product_uncalibrated'; productId: string; productName: string }
  | {
      code: 'position_uncalibrated';
      productId: string;
      productName: string;
      positionId: string;
      positionSlug: string;
      positionName: string;
    };

export interface Design {
  id: string;
  userId: string;
  name: string;
  description?: string;
  canvasWidth: number;
  canvasHeight: number;
  backgroundColor: string;

  /**
   * Fabric.js canvas JSON - Source of Truth for design data
   * Use canvas.toJSON() to serialize, canvas.loadFromJSON() to restore
   */
  canvasData?: Record<string, unknown>;

  thumbnailUrl?: string;
  /**
   * Rendered product image of the listing (master product, front, first
   * colour), null until a render finished. The overview shows it instead of
   * the bare design.
   */
  coverImageUrl?: string | null;
  tags?: string[];
  publishCategory?: string;
  publishCollection?: string;
  channelName?: string;
  /** Channel state of this listing; empty for listings never sent anywhere. */
  channelPublications?: DesignChannelPublication[];
  /**
   * True while a channel job is writing this listing to a shop. The creator
   * stops saving meanwhile: a retry re-reads the design, so an edit in
   * between would put mixed versions into the shop (plan KTD13).
   */
  publishRunning?: boolean;
  /**
   * Status the overview shows. Derived from channel state, because
   * `status` flips to ACTIVE on click, before anything was sent.
   */
  derivedStatus?: 'draft' | 'completed' | 'published';
  /** Why the overview may not send this listing; null when it may. */
  sendBlockReason?:
    | 'missing_title'
    | 'no_active_variant'
    | 'unpriced_product'
    | 'negative_profit'
    | 'product_uncalibrated'
    | 'position_uncalibrated'
    | null;
  /**
   * For `negative_profit`: the first product whose price does not cover its
   * cost and the gross minimum price (EUR) that would. For the calibration
   * reasons: the product and, for a position, its localised name. Null
   * otherwise.
   */
  sendBlockDetail?: {
    productName: string;
    minimumPrice?: number;
    positionName?: string;
  } | null;
  /**
   * Why the products keep this design from going live (R13, R31): a product
   * that is no longer selectable, or a saved position that is no longer set
   * up for printing. Only on the single-design read; names are localised.
   */
  calibrationBlockers?: CalibrationBlocker[];
  status: DesignStatus;
  createdAt: string;
  updatedAt: string;
  /** When the design was last published to the shop; null while unpublished. */
  publishedAt?: string | null;
  /** How the design was authored; 'guided' re-opens in the guided entry. */
  createdVia?: string | null;
  /**
   * Review-gate state of MCP-authored drafts; null for designs outside
   * the review flow. NEEDS_REVIEW and PUBLISH_FAILED belong to the review
   * queue, APPROVED is the state during a running publish job.
   */
  reviewState?: 'NEEDS_REVIEW' | 'APPROVED' | 'PUBLISH_FAILED' | null;
  /** Product id of the Master Product; null falls back to the heuristic */
  masterProductId?: string | null;
  /**
   * The seller changed the design since its last publish froze it: print
   * and shop follow only with a new publish. Always false before the first.
   */
  hasUnpublishedChanges?: boolean;

  // Relations (optional, for metadata/search only)
  products?: DesignProduct[];
  elements?: DesignElement[]; // Denormalized for search, NOT used for loading
  versions?: DesignVersion[];
}

export interface DesignElement {
  id: string;
  type: 'TEXT' | 'IMAGE' | 'SHAPE' | 'SVG' | 'CLIPART' | 'GROUP';
  name?: string;
  layerOrder: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
  fill?: string;
  properties: Record<string, unknown>;
  isPersonalizable: boolean;
  isLocked: boolean;
  isVisible: boolean;
}

export interface DesignVersion {
  id: string;
  versionNumber: number;
  canvasData?: Record<string, unknown>;
  createdAt: string;
}

export interface DesignProduct {
  id: string;
  productId: string;
  isEnabled: boolean;
  /** ISO timestamp of DesignProduct creation, used to pick a deterministic master product */
  createdAt?: string;
  product?: {
    id: string;
    name: string;
    slug: string;
    mockupImageUrl?: string;
    /** Carry-over class; the master choice and the rule read it live. */
    productClass?: ProductClass | null;
    carryOverPercent?: number | null;
    carryOverAnchor?: CarryOverAnchor | null;
    carryOverTopOffsetMm?: number | null;
  };
  /**
   * One selling price per product (pricing model of 2026-09-19), a decimal
   * string; null while the seller has not priced the product.
   */
  sellingPrice?: string | null;
  /** Snapshot of sellingPrice minus seller cost at the last save; null = unknown. */
  profit?: string | null;
  /** Snapshot of the decoration cost at the last save; null = unknown. */
  decorationCost?: string | null;
  colors?: DesignProductColor[];
  positions?: DesignPosition[];
}

export interface DesignProductColor {
  id: string;
  productColorId: string;
  /** @deprecated Mirror of the product's sellingPrice for one release; read it from the product. */
  sellingPrice?: number | null;
  productColor?: {
    id: string;
    name: string;
    hexCode: string;
  };
}

export interface DesignPosition {
  id: string;
  productPositionId: string;
  decorationMethodId?: string;
  canvasData?: Record<string, unknown>;
  scale: number;
  offsetX: number;
  offsetY: number;
  useColorSpecificDesign: boolean;
  /**
   * The design box in mm on this position's own print area (R17); null
   * when the position is empty or its area has no millimetres.
   */
  placementMm?: PlacementMm | null;
  /** S3 URL for tight-crop PNG of design objects - Stufe 4.3 */
  designCropUrl?: string;
  /** Whether this placement was manually adjusted (true) or auto-computed (false) */
  isCustomPlacement?: boolean;
  productPosition?: {
    id: string;
    name: string;
    slug: string;
  };
  decorationMethod?: {
    id: string;
    code: string;
    name: string;
  };
  colorDesigns?: ColorSpecificDesign[];
}

export interface ColorSpecificDesign {
  id: string;
  productColorId: string;
  canvasData?: Record<string, unknown>;
  scale?: number;
  offsetX?: number;
  offsetY?: number;
}

// ═══════════════════════════════════════════════════════════════════
// DTOs
// ═══════════════════════════════════════════════════════════════════

export interface CreateDesignDto {
  name: string;
  description?: string;
  canvasWidth?: number;
  canvasHeight?: number;
  backgroundColor?: string;
  canvasData?: Record<string, unknown>;
  thumbnailUrl?: string;
  tags?: string[];
  publishCategory?: string;
  publishCollection?: string;
  channelName?: string;
  /** How the design was authored; 'guided' re-opens in the guided entry. */
  createdVia?: string;
}

export interface UpdateDesignDto extends Partial<CreateDesignDto> {
  status?: DesignStatus;
}

export interface MarketplacePublishResponse {
  success: boolean;
  seller_id?: string;
  product_ids?: string[];
  message?: string;
}

export interface DesignListQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: DesignStatus;
  sortBy?: 'createdAt' | 'updatedAt' | 'name';
  sortOrder?: 'asc' | 'desc';
  /** Hide review-queue drafts (NEEDS_REVIEW, PUBLISH_FAILED) server-side. */
  excludeReviewQueue?: boolean;
}

export interface ProductColorConfig {
  productColorId: string;
}

export interface PositionConfig {
  productPositionId: string;
  /** Omitted keeps the stored method; null clears it. */
  decorationMethodId?: string | null;
  /** Canvas JSON in millimetre space on this position's print area (R34). */
  canvasData?: Record<string, unknown>;
  useColorSpecificDesign?: boolean;
  /**
   * The design box in mm on this position's print area (R17). Omitted: the
   * server derives it from the canvas JSON when canvasData is sent (an
   * empty canvas clears it), else the stored placement stays. The pixel-era
   * placement fields (scale, offsets, the pixel context, the relative
   * state, designWidthMm) are refused with 400.
   */
  placementMm?: PlacementMm;
  /** S3 URL for tight-crop PNG of design objects - Stufe 4.3 */
  designCropUrl?: string;
  /** Whether this placement was manually adjusted (true) or auto-computed (false) */
  isCustomPlacement?: boolean;
  /** Guided entry: preset id, 'custom' for free placement, null when canvas-authored */
  placementPresetId?: string | null;
  colorDesigns?: {
    productColorId: string;
    canvasData?: Record<string, unknown>;
  }[];
}

export interface ProductConfig {
  productId: string;
  isEnabled?: boolean;
  /**
   * The product's selling price. Omitted keeps the stored price (patch
   * semantics); never 0 — the server refuses a non-positive price.
   */
  sellingPrice?: number;
  colors?: ProductColorConfig[];
  positions?: PositionConfig[];
}

export interface SaveDesignConfigurationDto {
  products: ProductConfig[];
  /** Recorded on the first save only; later saves never move it */
  masterProductId?: string;
}

// ═══════════════════════════════════════════════════════════════════
// PRICING (GET /designs/:id/pricing)
// ═══════════════════════════════════════════════════════════════════

/**
 * What the Creator shows per product: seller cost, profit and the surcharge
 * per variant, quoted by the API from the stored design state. Money travels
 * as decimal strings; the raw blank price per variant never reaches the
 * client, only its surcharge above the blank reference price.
 */
export interface DesignVariantPricing {
  variantId: string;
  productColorId: string | null;
  /** Size code of the variant (e.g. "3XL"); null when the catalogue has none. */
  sizeCode: string | null;
  /** Amount above the blank reference price; null when the blank is unknown. */
  surcharge: string | null;
  /** What the end customer pays for this variant; null while unpriced. */
  price: string | null;
}

export interface DesignPositionPricing {
  productPositionId: string;
  decorationMethodId: string | null;
  price: string | null;
  source: 'product_override' | 'platform_base' | 'unpriced' | 'no_method';
}

export interface DesignProductPricing {
  designProductId: string;
  productId: string;
  sellingPrice: string | null;
  /** Blank reference price (mode over sellable variants). */
  modusBlank: string | null;
  blankSource:
    | 'customer_price'
    | 'variant_price'
    | 'product_price'
    | 'mixed'
    | 'unknown';
  decorationCost: string | null;
  /** Seller cost, net: blank reference price plus decoration. */
  sellerCost: string | null;
  /** Net selling price minus sellerCost (net); may be negative. */
  profit: string | null;
  /** max(recommendedSalesPrice, gross sellerCost); the seed for an unpriced product. */
  suggestedStartPrice: string | null;
  /**
   * VAT rate in percent ("19") the server used to bring the gross selling
   * price to net. Optional: an API build from before the net calculation
   * answers without it during a deploy window; the client then converts
   * with the fixed 19 % (`DEFAULT_VAT_RATE`).
   */
  vatRate?: string;
  /** sellingPrice net of VAT; null when unpriced. Optional like `vatRate`. */
  netSellingPrice?: string | null;
  positions: DesignPositionPricing[];
  variants: DesignVariantPricing[];
}

export interface DesignPricing {
  designId: string;
  products: DesignProductPricing[];
}

// ═══════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Get all designs for current user
 */
export async function getDesigns(
  query?: DesignListQuery
): Promise<PaginatedResponse<Design>> {
  const params = new URLSearchParams();
  if (query?.page) params.append('page', query.page.toString());
  if (query?.limit) params.append('limit', query.limit.toString());
  if (query?.search) params.append('search', query.search);
  if (query?.status) params.append('status', query.status);
  if (query?.sortBy) params.append('sortBy', query.sortBy);
  if (query?.sortOrder) params.append('sortOrder', query.sortOrder);
  if (query?.excludeReviewQueue) params.append('excludeReviewQueue', 'true');

  const response = await apiClient.get<ApiResponse<PaginatedResponse<Design>>>(
    `/designs?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Get a single design by ID
 */
export async function getDesign(id: string): Promise<Design> {
  const response = await apiClient.get<ApiResponse<Design>>(`/designs/${id}`);
  return response.data.data;
}

/**
 * Create a new design
 */
export async function createDesign(data: CreateDesignDto): Promise<Design> {
  const response = await apiClient.post<ApiResponse<Design>>('/designs', data);
  return response.data.data;
}

/**
 * Update a design
 */
export async function updateDesign(
  id: string,
  data: UpdateDesignDto
): Promise<Design> {
  const response = await apiClient.patch<ApiResponse<Design>>(
    `/designs/${id}`,
    data
  );
  return response.data.data;
}

export async function publishDesignToMarketplace(
  id: string
): Promise<MarketplacePublishResponse> {
  const response = await apiClient.post<
    ApiResponse<MarketplacePublishResponse>
  >(`/onboarding/designs/${id}/publish`);
  return response.data.data;
}

/**
 * Delete a design
 */
export async function deleteDesign(id: string): Promise<void> {
  await apiClient.delete(`/designs/${id}`);
}

/**
 * Duplicate a design
 */
export async function duplicateDesign(id: string): Promise<Design> {
  const response = await apiClient.post<ApiResponse<Design>>(
    `/designs/${id}/duplicate`
  );
  return response.data.data;
}

/**
 * Get the pricing quote of every product of a design
 */
export async function getDesignPricing(id: string): Promise<DesignPricing> {
  const response = await apiClient.get<ApiResponse<DesignPricing>>(
    `/designs/${id}/pricing`
  );
  return response.data.data;
}

/**
 * Get design versions
 */
export async function getDesignVersions(id: string): Promise<DesignVersion[]> {
  const response = await apiClient.get<ApiResponse<DesignVersion[]>>(
    `/designs/${id}/versions`
  );
  return response.data.data;
}

// ═══════════════════════════════════════════════════════════════════
// DESIGN PRODUCTS API
// ═══════════════════════════════════════════════════════════════════

/**
 * Get all products for a design
 */
export async function getDesignProducts(
  designId: string
): Promise<DesignProduct[]> {
  const response = await apiClient.get<ApiResponse<DesignProduct[]>>(
    `/designs/${designId}/products`
  );
  return response.data.data;
}

/**
 * Add a product to a design
 */
export async function addProductToDesign(
  designId: string,
  productId: string,
  isEnabled = true
): Promise<DesignProduct> {
  const response = await apiClient.post<ApiResponse<DesignProduct>>(
    `/designs/${designId}/products`,
    { productId, isEnabled }
  );
  return response.data.data;
}

/**
 * Remove a product from a design
 */
export async function removeProductFromDesign(
  designId: string,
  productId: string
): Promise<void> {
  await apiClient.delete(`/designs/${designId}/products/${productId}`);
}

/**
 * Set the colours of a product and, optionally, its selling price
 */
export async function setProductColors(
  designId: string,
  productId: string,
  colors: ProductColorConfig[],
  sellingPrice?: number
): Promise<DesignProductColor[]> {
  const response = await apiClient.patch<ApiResponse<DesignProductColor[]>>(
    `/designs/${designId}/products/${productId}/colors`,
    { colors, ...(sellingPrice !== undefined && { sellingPrice }) }
  );
  return response.data.data;
}

/**
 * Update position configuration
 */
export async function updateDesignPosition(
  designId: string,
  productId: string,
  positionId: string,
  config: Partial<PositionConfig>
): Promise<DesignPosition> {
  const response = await apiClient.patch<ApiResponse<DesignPosition>>(
    `/designs/${designId}/products/${productId}/positions/${positionId}`,
    config
  );
  return response.data.data;
}

/**
 * Set color-specific design
 */
export async function setColorSpecificDesign(
  designId: string,
  productId: string,
  positionId: string,
  colorData: {
    productColorId: string;
    canvasData?: Record<string, unknown>;
    scale?: number;
    offsetX?: number;
    offsetY?: number;
  }
): Promise<ColorSpecificDesign> {
  const response = await apiClient.post<ApiResponse<ColorSpecificDesign>>(
    `/designs/${designId}/products/${productId}/positions/${positionId}/color-designs`,
    colorData
  );
  return response.data.data;
}

/**
 * Delete color-specific design
 */
export async function deleteColorSpecificDesign(
  designId: string,
  productId: string,
  positionId: string,
  colorId: string
): Promise<void> {
  await apiClient.delete(
    `/designs/${designId}/products/${productId}/positions/${positionId}/color-designs/${colorId}`
  );
}

/**
 * Save complete design configuration (batch)
 */
export async function saveDesignConfiguration(
  designId: string,
  config: SaveDesignConfigurationDto
): Promise<Design> {
  const response = await apiClient.post<ApiResponse<Design>>(
    `/designs/${designId}/configuration`,
    config
  );
  return response.data.data;
}

// ═══════════════════════════════════════════════════════════════════
// THUMBNAIL API
// ═══════════════════════════════════════════════════════════════════

export interface ThumbnailResponse {
  thumbnailUrl: string;
}

/**
 * Upload design thumbnail
 * The thumbnail is resized to 400px and converted to WebP on the backend.
 *
 * @param designId - Design ID
 * @param thumbnailBlob - Thumbnail image as Blob (any format)
 * @returns The public URL of the uploaded thumbnail
 */
export async function uploadDesignThumbnail(
  designId: string,
  thumbnailBlob: Blob
): Promise<ThumbnailResponse> {
  const formData = new FormData();
  formData.append('thumbnail', thumbnailBlob, 'thumbnail.webp');

  const response = await apiClient.post<ApiResponse<ThumbnailResponse>>(
    `/designs/${designId}/thumbnail`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data.data;
}
